import jsPDF from 'jspdf';
import boldFont from '../constants/fonts/open-sans-bold';
import regularFont from '../constants/fonts/open-sans-regular';
import { PDF_FONT, PDF_TEXT_LINE_MARGIN } from '../constants/pdf';
import { AddTextOptions, GetTextSizeOptions, PdfGeneratorOptions } from '../types/pdf';

export class PdfGenerator {
	private file: jsPDF;
	private yPosition: number;

	constructor(private readonly options: PdfGeneratorOptions) {
		this.yPosition = options.yPadding;
		this.file = new jsPDF({ compress: options.compress, unit: 'px' });
	}

	private clear() {
		this.yPosition = this.options.yPadding;
		this.file = new jsPDF({ compress: this.options.compress, unit: 'px' });
	}

	async addFonts() {
		this.file.addFileToVFS(`${PDF_FONT}-Bold.ttf`, boldFont);
		this.file.addFont(`${PDF_FONT}-Bold.ttf`, PDF_FONT, 'bold');

		this.file.addFileToVFS(`${PDF_FONT}-Regular.ttf`, regularFont);
		this.file.addFont(`${PDF_FONT}-Regular.ttf`, PDF_FONT, 'normal');
	}

	getYPosition(blockHeight = 0) {
		const fileHeight = this.file.internal.pageSize.height;
		if (this.yPosition + blockHeight > fileHeight - this.options.yPadding) {
			this.file.addPage();
			this.yPosition = this.options.yPadding;
		}

		return this.yPosition;
	}

	updateYPosition(blockHeight: number) {
		const fileHeight = this.file.internal.pageSize.height;
		if (this.yPosition + blockHeight > fileHeight - this.options.yPadding) {
			this.file.addPage();
			this.yPosition = this.options.yPadding;
		} else {
			this.yPosition += blockHeight;
		}
	}

	getFileSize() {
		const pageSize = this.file.internal.pageSize;

		return {
			...pageSize,
			contentWidth: pageSize.width - this.options.xPadding * 2,
			contentHeight: pageSize.height - this.options.yPadding * 2,
		};
	}

	getTextSize({ text, fontSize, fontStyle = 'normal' }: GetTextSizeOptions) {
		this.file.setFontSize(fontSize);
		this.file.setFont(PDF_FONT, fontStyle);

		const textSize = this.file.getTextDimensions(text);

		return {
			width: textSize.w,
			height: textSize.h,
		};
	}

	addText({
		fontSize,
		text,
		topMargin = 0,
		yPosition,
		xPosition,
		fontStyle = 'normal',
	}: AddTextOptions) {
		if (typeof yPosition !== 'number') {
			this.yPosition = topMargin + this.yPosition;
		}

		this.file.setFontSize(fontSize);
		this.file.setFont(PDF_FONT, fontStyle);
		const textChunks: string[] = this.file.splitTextToSize(
			text,
			this.file.internal.pageSize.width - this.options.xPadding * 2,
		);

		for (let i = 0; i < textChunks.length; i++) {
			const chunk = textChunks[i];
			const textHeight = this.file.getTextDimensions(chunk).h + PDF_TEXT_LINE_MARGIN;

			const topPosition =
				typeof yPosition === 'number'
					? yPosition + (PDF_TEXT_LINE_MARGIN + textHeight) * i
					: this.yPosition;

			this.file.text(
				chunk,
				typeof xPosition === 'number' ? xPosition : this.options.xPadding,
				topPosition,
			);

			if (typeof yPosition !== 'number') {
				const additionalMargin = i === textChunks.length - 1 ? 0 : PDF_TEXT_LINE_MARGIN;
				this.yPosition = textHeight + additionalMargin + this.yPosition;
			}
		}
	}

	download(fileName: string) {
		this.file.save(fileName);
		this.clear();
	}
}
